import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { Premises } from '../../model/premises';
import { PremisesRentalObjectCommandFactory } from '../../service/command/factory/premises-rental-object-command-factory.service';
import { PremisesCommandHandlerResolver } from '../../service/command/premises-command-handler-resolver ';
import { BuildingService } from '../../service/data/building.service';
import { CompanyService } from '../../service/data/company.service';
import { EntranceService } from '../../service/data/entrance.service';
import { PremisesService } from '../../service/data/premises.service';
import { PropertyService } from '../../service/data/property.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { CustomPropertyVisibilityType, FormOfLease, FormOfLeaseToString } from '../../model/const/enums';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { LettingFeatureFilterHelper } from '../../helper/letting-feature-filter-helper';
import { LettingFeatureColumnHelper } from '../../helper/letting-feature-column-helper';
import { LettingFeatureService } from '../../service/data/letting-feature.service';
import { standardCommand } from '../../../core/model/command';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-premises-rental-object-manager',
    templateUrl: 'premises-rental-object-manager.component.html',
    styleUrls: [
        'premises-rental-object-manager.component.scss',
        './../../../core/component/multiselect-object-manager/multiselect-object-manager.component.scss',
    ],
    providers: [PremisesCommandHandlerResolver, { provide: CommandFactoryService, useClass: PremisesRentalObjectCommandFactory }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PremisesRentalObjectManagerComponent extends PagedObjectManagerComponent<Premises> {
  constructor(
    private readonly cdf: ColumnDefinitionFactory,
    private _dataPremisesService: PremisesService,
    dispatcher: CommandDispatcher,
    messageService: MessageService,
    private _companyService: CompanyService,
    private _buildingService: BuildingService,
    private _propertyService: PropertyService,
    private _entranceService: EntranceService,
    private _accountService: UserAccountService,
    navigationService: NavigationService,
    handlerResolver: PremisesCommandHandlerResolver,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _lettingFeatureService: LettingFeatureService,
    private readonly _lettingFeatureFilterHelper: LettingFeatureFilterHelper,
    private readonly _lettingFeatureColumnHelper: LettingFeatureColumnHelper
  ) {
    super(_dataPremisesService, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      cdf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      cdf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      cdf.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      cdf.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      cdf.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      cdf.create('COMMON.BUILDING_NUMBER', 'buildingNumber', ColumnVisibility.optional),
      cdf.create('COMMON.BUILDING', 'buildingName', ColumnVisibility.optional),
      cdf.createNumber('RENTAL_OBJECT_FORM.SURFACE_LOA_LONG', 'surface', { visibility: ColumnVisibility.default, totalSum: true }),
      cdf.create('DWELLING_FORM.FLOOR_LABEL', 'floor'),
      cdf.create('RENTAL_OBJECT_TYPE_FORM.NUMBER_OF_ROOMS_LABEL', 'numberOfRooms', ColumnVisibility.optional),
      cdf.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      cdf.create('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE', 'tenantOneName'),
      cdf.createPhoneNumber('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE_PHONE', 'tenantOnePhone', ColumnVisibility.optional),
      cdf.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      cdf.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', ColumnVisibility.optional),
      cdf.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', ColumnVisibility.optional),
      cdf.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      cdf.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      cdf.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      cdf.createTranslation('RENTAL_OBJECT.FORM_OF_LEASE', 'formOfLeaseText', ColumnVisibility.optional),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent', {
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN_LEASE_AGREEMENT', 'currentMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_CURRENT_COLUMN', 'rentLossCurrentMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN', 'monthlyRentNext', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN_LEASE_AGREEMENT', 'nextMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_NEXT_COLUMN', 'rentLossNextMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('COMMON.YEAR_RENT', 'yearRent', {
        allowSort: this,
      }),
      cdf.createMoney('COMMON.YEAR_RENT_PER_SQM', 'yearRentPerSqm', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'yearRentIncludingVat', {
        allowSort: this,
      }),
      cdf.create('DWELLING_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL', 'indvidualMeterElectricityText', ColumnVisibility.optional),
      cdf.create('DWELLING_FORM.INDIVIDUAL_METER_WATER_LABEL', 'indvidualMeterWaterText', ColumnVisibility.optional),
      cdf.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      cdf.createMoney('COMMON.RENT_LOSS_PREVIOUS_YEAR', 'rentLossPreviousYear', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
    ];

    this.preferencesKey = 'rent.premises-rental-object-manager.list';

    this.menuDefinition = [new MenuItemDefinition('LEASE_AGREEMENT.COMMAND.EXPORT', rentalObjectCommand.export)];
    this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.RENTAL_CHANGE', rentalObjectCommand.feeChange);

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Properties, Features.EditPremises)) {
            this.allowNew = true;
          } else {
            this.allowNew = false;
          }
          if (account.hasAccess(ModuleNames.Properties, Features.ChangeRentalObjectCategory)) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT_CATEGORY.COMMAND', rentalObjectCommand.changeCategory);
          }

          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', rentalObjectCommand.showInvoices);
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditPremises)
          ) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', rentalObjectCommand.linkDocument);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_DOCUMENTS', rentalObjectCommand.showDocuments);

          if (account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
          }

          this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
          if (this.allowNew) {
            this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
        },
      })
    );

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
          }
        },
      })
    );

    this.title = 'RENTAL_OBJECT_LIST.PREMISES_HEADER';
  }
  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
    this._lettingFeatureFilterHelper.applyLettingFeatureFilters(filterState);
  }

  ngOnInit() {
    this.AddSubscription(
      forkJoin([this._customPropertyService.getList(), this._dataPremisesService.getFilterDefinition(), this._lettingFeatureService.getAvailableForRentalObjects()]).subscribe(
        ([customProperties, definition, lettingFeatures]) => {
          this.defaultFilters = definition.defaultFields;
          const rentalObjectTypes = definition.rentalObjectTypes.map(x => {
            return { caption: x.name, value: x.id };
          });
          rentalObjectTypes.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
          const articles = definition.articles.map(article => {
            return { caption: article.toString(), value: article.id };
          });
          articles.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

          var formOfLeaseSelection = [
            { caption: 'ALL_TEXT', value: null },
            { caption: FormOfLeaseToString(FormOfLease.Lease), value: FormOfLease.Lease },
            { caption: FormOfLeaseToString(FormOfLease.CooperativeHousing), value: FormOfLease.CooperativeHousing },
            { caption: FormOfLeaseToString(FormOfLease.Ownership), value: FormOfLease.Ownership },
          ];

          const selectedCompanies = definition.companyIds
            ? Array.isArray(definition.companyIds)
              ? definition.companyIds
              : [definition.companyIds]
            : [];

          const selectedProperties = definition.propertyIds
            ? Array.isArray(definition.propertyIds)
              ? definition.propertyIds
              : [definition.propertyIds]
            : [];

          const selectedBuildings = definition.buildingIds
            ? Array.isArray(definition.buildingIds)
              ? definition.buildingIds
              : [definition.buildingIds]
            : [];

          const selectedEntrances = definition.entranceIds
            ? Array.isArray(definition.entranceIds)
              ? definition.entranceIds
              : [definition.entranceIds]
            : [];

          this.filterDefinition = [
            new FilterDefinitionItem(
              'COMPANY.LIST_HEADER',
              'companyIds',
              selectedCompanies,
              this._companyService,
              definition.selectedFields.includes('companyIds') || definition.selectedFields.includes('company'),
              true
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
              'propertyIds',
              selectedProperties,
              this._propertyService,
              definition.selectedFields.includes('propertyIds') || definition.selectedFields.includes('property'),
              true
            ),
            new FilterDefinitionItem(
              'BUILDING_FORM.EDIT_HEADER',
              'buildingIds',
              selectedBuildings,
              this._buildingService,
              definition.selectedFields.includes('buildingIds') || definition.selectedFields.includes('building'),
              true
            ),
            new FilterDefinitionItem(
              'ENTRANCE.ENTRANCE',
              'entranceIds',
              selectedEntrances,
              this._entranceService,
              definition.selectedFields.includes('entranceIds') || definition.selectedFields.includes('entrance'),
              true
            ),
            new FilterDefinitionItem(
              'COMMON.RENTAL_OBJECT_TYPE',
              'rentalObjectType',
              definition.rentalObjectType,
              rentalObjectTypes,
              definition.selectedFields.includes('rentalObjectType')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
              'rentalId',
              definition.rentalId,
              null,
              definition.selectedFields.includes('rentalId')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_LIST.STREET_COLUMN',
              'street',
              definition.street,
              null,
              definition.selectedFields.includes('street')
            ),
            new FilterDefinitionItem(
              'ARTICLE.EDIT_HEADER',
              'article',
              definition.article,
              articles,
              definition.selectedFields.includes('article')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT.FORM_OF_LEASE',
              'formOfLease',
              definition.formOfLease,
              formOfLeaseSelection,
              definition.selectedFields.includes('formOfLease')
            ),
          ];

          const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
            customProperties,
            definition,
            CustomPropertyVisibilityType.Company |
              CustomPropertyVisibilityType.Property |
              CustomPropertyVisibilityType.Building |
              CustomPropertyVisibilityType.Premises
          );
          this._ch.createCustomPropertyColumnDefinition(
            customProperties,
            this.columnDefinition,
            CustomPropertyVisibilityType.Company |
              CustomPropertyVisibilityType.Property |
              CustomPropertyVisibilityType.Building |
              CustomPropertyVisibilityType.Premises
          );
          this._lettingFeatureColumnHelper.createLettingFeatureColumnDefinitions(lettingFeatures, this.columnDefinition);
          const lettingFeatureFilters = this._lettingFeatureFilterHelper.generateLettingFeatureFilters(lettingFeatures, definition);
          this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters, ...lettingFeatureFilters];
        }
      )
    );
  }
}
